import { findModeConfig } from "~/shared/data/chat-models";
import { resolutionLabel } from "~/shared/lib/resolution";

import type { ArtifactType, GenerationMode, ModelConfig } from "~/shared/data/chat-models";

/**
 * Форматирует подпись send-кнопки из live-quote цены.
 *
 * `n <= 0` (или NaN / отсутствие цены) трактуем как «цена не настроена в
 * БД» — показываем просто generate-лейбл, а не «$0.00» (последнее ввело бы
 * пользователя в заблуждение «бесплатно»). Пороги форматирования: < $10 —
 * два знака, < $100 — один, иначе округление до целого.
 */
export function formatCostLabel(totalPriceUsd: string | undefined, generateLabel: string): string {
    if (!totalPriceUsd) return generateLabel;
    const n = Number.parseFloat(totalPriceUsd);
    if (!Number.isFinite(n) || n <= 0) return generateLabel;
    const formatted = n < 10 ? `$${n.toFixed(2)}` : n < 100 ? `$${n.toFixed(1)}` : `$${Math.round(n)}`;
    return `${generateLabel} ~ ${formatted}`;
}

/** Почему кнопка генерации не работает — что показать человеку. */
export interface SendBlockHint {
    readonly key: string;
    /** Подстановка для плюрализованных ключей; null — ключ без счётчика. */
    readonly count: number | null;
}

export interface SendBlockState {
    /** Режим требует обязательных кадров, которых пока нет. */
    readonly missingRequiredImages: boolean;
    /** Режим требует визуального входа (референс или видео), которого пока нет. */
    readonly missingVisualInput: boolean;
    /** Выбран хотя бы один элемент библиотеки. */
    readonly hasElements: boolean;
    /** Цена отказала именно из-за набранного состава входов. */
    readonly inputsNotExecutable: boolean;
    /** Длина входного видео ещё измеряется — показанная цена без него, неполная. */
    readonly priceIncomplete: boolean;
    readonly minImages: number;
}

/**
 * Единственная причина, которую стоит показать на кнопке.
 *
 * Порядок — от того, что человек правит первым делом, к общему запрету набора:
 * пока не хватает обязательных кадров, говорить «этот набор никто не исполняет»
 * рано (набор ещё не собран). Элементы получают свой текст: общее «нужно ещё
 * изображение» при занятом слоте элемента читается как ошибка интерфейса.
 */
export function sendBlockHint(state: SendBlockState): SendBlockHint | null {
    if (state.missingRequiredImages) {
        return state.hasElements
            ? { key: "prompt.requireFrameWithElements", count: null }
            : { key: "prompt.requireImages", count: state.minImages };
    }
    // Отдельная причина, а не «нужно ещё изображение»: у мультимодального режима
    // кадров нет, и человеку надо сказать, что подойдёт и картинка-референс, и видео.
    if (state.missingVisualInput) return { key: "prompt.requireVisualInput", count: null };
    // Ожидание, а не ошибка набора: тариф считает входное видео вместе с выходом,
    // и пока его длина не измерена, на кнопке стоит цена БЕЗ входа. Отправка в это
    // окно списала бы больше показанного — ждём замер, набор менять не нужно.
    if (state.priceIncomplete) return { key: "prompt.priceIncomplete", count: null };
    if (state.inputsNotExecutable) return { key: "prompt.inputsNotExecutable", count: null };
    return null;
}

export interface SettingsSummaryInput {
    model: ModelConfig | undefined;
    mode: GenerationMode;
    aspectRatio: string;
    resolution: number;
    artifactType: ArtifactType;
    duration: number;
    batchSize: number;
}

/**
 * Собирает строки-чипсы для тултипа текущих настроек генерации
 * (бренд/модель · режим · соотношение · разрешение · длительность · батч).
 * Длительность показываем только для видео с duration > 0, батч — только
 * когда > 1.
 *
 * Модели нет (каталог пуст — все провайдеры выключены; либо настройки ещё не
 * пришли) — описывать нечего: возвращаем пустой список. Иначе подпись показывала
 * бы сырой ключ режима, а соотношение и разрешение — значения от прошлой модели,
 * то есть настройки генерации, которую некому исполнить.
 */
export function buildSettingsSummary({
    model,
    mode,
    aspectRatio,
    resolution,
    artifactType,
    duration,
    batchSize,
}: SettingsSummaryInput): string[] {
    if (!model) return [];

    const modeConfig = findModeConfig(model, mode);
    const parts: string[] = [];
    // Лейблы бренда и режима — с бэка (data-driven).
    parts.push(`${model.brandLabel} / ${model.name}`);
    parts.push(modeConfig?.title ?? mode);
    // Ось, которой у режима нет, в подписи не место: апскейл не спрашивает ни
    // соотношения, ни разрешения, и панель настроек их не показывает — а чип
    // обещал «16:9 · 720p», то есть настройки, которые никуда не уедут.
    if (!modeConfig || modeConfig.aspectRatios.length > 0) parts.push(aspectRatio);
    if (!modeConfig || modeConfig.resolutions.length > 0) parts.push(resolutionLabel(resolution));
    const durationApplies = !modeConfig || modeConfig.durations.length > 0;
    if (durationApplies && artifactType === "video" && duration > 0) parts.push(`${duration}s`);
    if (batchSize > 1) parts.push(`x${batchSize}`);
    return parts;
}
